
import { useState } from 'react'
import { useLocation } from 'wouter'
import { Link } from 'wouter'
import { Flame, Eye, EyeOff, ArrowRight, Lock, Mail, Shield, Zap, Globe } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { PLATFORM_DOMAIN } from '@/lib/constants'

export default function LoginPage() {
  const [, setLocation] = useLocation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      })

      const data = await res.json()

      if (!res.ok) {
        setError(data.error || 'Authentication failed')
        return
      }

      setLocation('/dashboard')
    } catch {
      setError('Network error. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left — brand panel */}
      <div className="hidden lg:flex flex-col w-[480px] bg-card border-r border-border p-10 relative overflow-hidden">
        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)`,
            backgroundSize: '32px 32px',
          }}
        />

        {/* Glow orb */}
        <div
          className="absolute bottom-0 left-0 w-96 h-96 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, var(--glow-lava) 0%, transparent 70%)', transform: 'translate(-30%, 30%)' }}
        />

        {/* Brand */}
        <div className="flex items-center gap-3 relative z-10">
          <div className="w-9 h-9 rounded-lg lava-gradient flex items-center justify-center shadow-md glow-lava">
            <Flame className="w-5 h-5 text-lava-foreground" />
          </div>
          <div>
            <p className="font-bold text-foreground">LavaFlow Streams</p>
            <p className="text-xs text-muted-foreground font-mono">{PLATFORM_DOMAIN}</p>
          </div>
        </div>

        <div className="flex-1 flex flex-col justify-center relative z-10 space-y-8">
          <div>
            <Badge className="mb-4 bg-lava/10 text-lava border-lava/20 text-xs">
              Enterprise AI Platform
            </Badge>
            <h2 className="text-3xl font-bold text-foreground leading-tight text-balance">
              The EdgeSQL API gateway for AI at scale
            </h2>
            <p className="text-muted-foreground mt-3 text-sm leading-relaxed">
              Keyless Web Crypto authentication. OpenAI-compatible endpoints. Cloudflare D1, Workers AI, R2, and KV — all in one unified platform.
            </p>
          </div>

          <div className="space-y-3">
            {[
              { icon: Shield, title: 'Web Crypto Key Signing', desc: 'Zero-trust keyless API authentication via HMAC-SHA256' },
              { icon: Zap, title: 'AI Gateway Multi-Router', desc: 'Route to Cloudflare AI, OpenAI, Anthropic, or Ollama' },
              { icon: Globe, title: 'OpenAI API Compatible', desc: 'Drop-in replacement for any OpenAI SDK client' },
            ].map((feat) => (
              <div key={feat.title} className="flex items-start gap-3 p-3 rounded-lg bg-muted/40 border border-border/50">
                <div className="p-1.5 rounded-md bg-lava/10 flex-shrink-0">
                  <feat.icon className="w-3.5 h-3.5 text-lava" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-foreground">{feat.title}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{feat.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <p className="text-[10px] text-muted-foreground relative z-10">
          Powered by Cloudflare D1 · Workers AI · R2 · KV · Durable Objects
        </p>
      </div>

      {/* Right — auth form */}
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        {/* Mobile brand */}
        <div className="lg:hidden flex items-center gap-2.5 mb-8">
          <div className="w-8 h-8 rounded-lg lava-gradient flex items-center justify-center">
            <Flame className="w-4 h-4 text-lava-foreground" />
          </div>
          <p className="font-bold text-foreground">LavaFlow Streams</p>
        </div>

        <Card className="w-full max-w-md bg-card border-border shadow-sm">
          <CardHeader className="pb-4">
            <div>
              <h1 className="text-lg font-bold text-foreground">Sign in to your account</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Access your back office and API management panel
              </p>
            </div>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/8 border border-destructive/20 px-3 py-2.5 rounded-md">
                  <Lock className="w-3.5 h-3.5 flex-shrink-0" />
                  {error}
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs font-medium">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-9 h-9 text-sm bg-input border-border"
                    required
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-xs font-medium">Password</Label>
                  <Link href="/forgot-password" className="text-[11px] text-muted-foreground hover:text-lava transition-colors">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-9 pr-9 h-9 text-sm bg-input border-border"
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-9 lava-gradient text-lava-foreground font-medium gap-2 text-sm shadow-sm hover:opacity-90 transition-opacity border-0"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Authenticating...
                  </>
                ) : (
                  <>
                    Sign In <ArrowRight className="w-3.5 h-3.5" />
                  </>
                )}
              </Button>

              <Separator className="bg-border" />

              <p className="text-center text-xs text-muted-foreground">
                No account?{' '}
                <Link href="/register" className="text-lava hover:underline font-medium">
                  Create one
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>

        <p className="text-[10px] text-muted-foreground mt-6 text-center max-w-xs">
          Sessions secured with per-user JWT secrets via Cloudflare Durable Objects.
          Keys never stored in plaintext.
        </p>
      </div>
    </div>
  )
}
